import { formatDecimal, formatNamedDayTime, tt } from '@agos/shared'

/**
 * `/settings-backups` — the database copies on disk.
 *
 * **FB-151.** The paragraph at the top of the page describes a system that no longer exists:
 *
 * > Systém automaticky zálohuje celú databázu každú hodinu. **Zálohy sú uložené v cloudovom úložisku.**
 * > Uchováva sa **posledných 24 záloh** (cca 24 hodín dozadu).
 *
 * `BackupService`'s own docblock answers the first claim:
 *
 * > **On a volume rather than in object storage.** Legacy pushes these into Replit's bucket; Replit is gone
 * > from this system. A volume the operator already backs up is the honest equivalent and keeps the data
 * > inside the deployment rather than in a third party nobody chose.
 *
 * And the second is a count per **database**, not in total. `ScheduledJobs.backups()` runs
 * `forEachFranchise` **plus central**, each database keeping its own 24 because the filename carries the
 * database name — so a platform with three franchises holds up to 96 files, and this page lists only its own.
 *
 * **FB-152.** `formatBytes` writes `1.5 MB` on a Slovak page — a decimal point, the same defect as the
 * `toFixed` calls that put `54.1 %` on `/analytics`. It also divides by 1024 while labelling KB and MB, so
 * the number is KiB under a KB label.
 *
 * **And a failed download is a `window.alert`.** On a POS tablet, with an untranslated literal.
 */

export interface BackupFile {
  filename: string
  sizeBytes: number
  createdAt: string
}

/** `MAX_BACKUPS` in `BackupService`, per database rather than in total. */
export const RETAINED_PER_DATABASE = 24

export function backupsOf(payload: unknown): BackupFile[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const filename = typeof row.filename === 'string' ? row.filename : ''
    if (filename === '') return []
    return [
      {
        filename,
        sizeBytes: typeof row.sizeBytes === 'number' && row.sizeBytes >= 0 ? row.sizeBytes : 0,
        createdAt: typeof row.createdAt === 'string' ? row.createdAt : '',
      },
    ]
  })
}

/**
 * A file size a Slovak reader can read.
 *
 * Binary units with the labels that mean them — 1 024 bytes is a KiB, and calling it KB is the difference
 * between a 4,6 GB dump and a 4,3 GiB one. The decimal is a comma, through `formatDecimal`.
 */
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-'
  if (bytes < 1024) return `${formatDecimal(bytes, 0)} B`
  if (bytes < 1024 * 1024) return `${formatDecimal(bytes / 1024, 0)} KiB`
  if (bytes < 1024 * 1024 * 1024) return `${formatDecimal(bytes / 1024 / 1024, 1)} MiB`
  return `${formatDecimal(bytes / 1024 / 1024 / 1024, 2)} GiB`
}

/** The total on disk for this database, which is what the retention count is about. */
export function totalSize(backups: readonly BackupFile[]): number {
  return backups.reduce((sum, backup) => sum + backup.sizeBytes, 0)
}

export type ListState = 'none-yet' | 'partial' | 'full'

/**
 * Where this database's retention stands.
 *
 * An empty list after being told *every hour, twenty-four kept* reads as *backups are broken*, and it is the
 * only reading available on a page that does not distinguish the two. It might equally be a deployment where
 * the hourly job has not run yet.
 */
export function listState(backups: readonly BackupFile[]): ListState {
  if (backups.length === 0) return 'none-yet'
  return backups.length >= RETAINED_PER_DATABASE ? 'full' : 'partial'
}

export function listMessage(state: ListState, count: number): string {
  if (state === 'none-yet') {
    return tt(
      'Zatiaľ tu nie je ani jedna záloha. Znamená to buď, že hodinová úloha ešte nebežala, alebo že nebeží - každý pokus sa zapisuje do systémového logu, tam sa to dá rozlíšiť.',
    )
  }
  if (state === 'full') {
    return `${tt('Drží sa maximum')} - ${RETAINED_PER_DATABASE} ${tt('záloh; každá ďalšia prepíše najstaršiu.')}`
  }
  return `${count} ${tt('z')} ${RETAINED_PER_DATABASE} ${tt('záloh tejto databázy. Ďalšie pribudnú každú hodinu.')}`
}

/**
 * When the hourly job last ran, and whether it worked — FB-151.
 *
 * The page used to point at the system log and say the answer was in there; `GET /backups/last-run`
 * reads it, so the page can state it instead. An empty file list is the same answer for *has not run
 * yet* and *stopped working three weeks ago*, and only the second one matters.
 */
export interface LastRun {
  ranAt: string | null
  ok: boolean
  message: string | null
}

export function lastRunOf(payload: unknown): LastRun {
  const row = (payload ?? {}) as Record<string, unknown>
  return {
    ranAt: typeof row.ranAt === 'string' && row.ranAt !== '' ? row.ranAt : null,
    ok: row.ok === true,
    message: typeof row.message === 'string' && row.message !== '' ? row.message : null,
  }
}

export type RunState = 'never' | 'failed' | 'ok'

export function runState(run: LastRun): RunState {
  if (run.ranAt === null) return 'never'
  return run.ok ? 'ok' : 'failed'
}

/**
 * Said in full, because the two bad states are not the same problem.
 *
 * *Never ran* is a job that was never switched on. *Ran and failed* is a job somebody believed was
 * protecting them. The server's own message is quoted rather than paraphrased — on the restored data it
 * reads *Object storage not configured — backup skipped*, which names the cause exactly.
 */
export function lastRunMessage(run: LastRun): string {
  const state = runState(run)
  if (state === 'never') {
    return tt('Úloha zálohovania sa zatiaľ nespustila ani raz - v systémovom logu po nej nie je stopa.')
  }
  const when = formatNamedDayTime(run.ranAt as string)
  if (state === 'failed') {
    return `${tt('Posledný pokus')} ${when} ${tt('zlyhal')}${run.message === null ? '.' : `: ${run.message}`}`
  }
  return `${tt('Naposledy prebehla')} ${when} ${tt('a podarila sa.')}`
}

/** What the storage really is, since the page used to say the other thing. */
export function storageNote(): string {
  return tt(
    'Zálohy sú na pripojenom disku servera, nie v cloudovom úložisku - to bolo v pôvodnom systéme. Uchováva sa posledných 24 záloh tejto databázy; ostatné databázy majú vlastných 24.',
  )
}

/**
 * Whether a filename is one this endpoint will serve.
 *
 * The server checks the shape before reading anything off the disk; checking it here too means a row the
 * server would refuse is not offered as a download.
 */
export function isServableFilename(filename: string): boolean {
  return /^[A-Za-z0-9_.-]+\.sql\.gz$/.test(filename) && !filename.includes('..')
}

/** The database a file came from, which is in the name because three tenants dump in the same second. */
export function databaseOf(filename: string): string | null {
  const match = /^backup-([A-Za-z0-9_]+)-/.exec(filename)
  return match?.[1] ?? null
}

export function restoreInstructions(): readonly string[] {
  return [
    tt('Stiahnite zálohu (.sql.gz).'),
    'gunzip backup-….sql.gz',
    'psql $DATABASE_URL < backup-….sql',
    tt('Obnova je manuálny zásah administrátora - nič ju nespustí samo.'),
  ]
}
